/**
 * Shadow-root style adoption.
 *
 * Vite's lib build normally extracts `import './styles.css'` into a stylesheet the
 * host links into `document.head` — where shadow content can never see it. The
 * element build instead pushes every stylesheet onto a global registry (see the
 * `injectCode` hook in `vite.element.config.ts`), and each element instance
 * adopts the collected CSS onto its own shadow root.
 */

/**
 * Version-scoped so two different releases of this package sharing a page do not
 * read or write each other's registry. `__FP_DW_VERSION__` is injected by Vite's
 * `define` from `package.json` at build time (see `vite.element.config.ts`); it
 * is undefined outside a Vite build (Vitest is given its own fixed value, see
 * `vitest.config.ts`), so this still resolves to something stable there.
 */
declare const __FP_DW_VERSION__: string | undefined;

const REGISTRY_VERSION =
  typeof __FP_DW_VERSION__ === 'string' ? __FP_DW_VERSION__ : 'dev';

/** Exported so tests and the build's generated prelude derive the same key. */
export const REGISTRY_KEY = `__FP_DOCUMENT_WORKSPACE_STYLES_V${REGISTRY_VERSION}__`;

/**
 * Host sizing plus media containment.
 *
 * Embedded in a host page the element gets a bounded box, so figures and tables
 * carrying their natural pixel width would force the whole column to scroll
 * horizontally. Constraining them is a concern of embedding, which is why it
 * lives here rather than in the viewer's own stylesheet.
 */
const HOST_STYLES = `:host{display:block;height:100%;width:100%;box-sizing:border-box}
:host([hidden]){display:none}
img,svg,video{max-width:100%;height:auto}
table{max-width:100%}`;

type StyleRegistry = Record<string, string[] | undefined>;

function registry(): string[] {
  const scope = globalThis as unknown as StyleRegistry;

  return (scope[REGISTRY_KEY] ??= []);
}

/**
 * Shadow roots that have already adopted the registry, and the live
 * sheet/element each one is holding, so a later registration can update them in
 * place (`replaceSync` / new `textContent`) instead of appending a duplicate.
 *
 * Scoped to this module instance: it only sees roots that connected through
 * *this* execution of the bundle. Two independent `<script>` tags loading the
 * same URL each get their own module scope and their own map, but both still
 * write to the same versioned `globalThis` registry, so a fresh root adopting
 * in either one always gets the complete, deduplicated CSS regardless of which
 * copy registered it.
 */
type AdoptedTarget =
  { kind: 'sheet'; sheet: CSSStyleSheet } | { kind: 'style'; element: HTMLStyleElement };

const adoptedRoots = new Map<ShadowRoot, AdoptedTarget>();

/**
 * Register a stylesheet manually.
 *
 * The element build's generated prelude mirrors this exact dedup check inline
 * (it cannot import this function — see `vite.element.config.ts`); keep the two
 * in sync if this logic changes. This export exists for tests and for consumers
 * assembling the element by hand.
 *
 * Any shadow root that already adopted the registry is updated in place, so
 * styles registered after an element has connected still reach it.
 */
export function registerStyles(css: string): void {
  const sheets = registry();

  if (!css || sheets.includes(css)) return;

  sheets.push(css);
  const full = getRegisteredStyles();

  for (const root of adoptedRoots.keys()) {
    applyToRoot(root, full);
  }
}

/** All CSS collected so far, host rules first. */
export function getRegisteredStyles(): string {
  return [HOST_STYLES, ...registry()].join('\n');
}

function supportsAdoptedStyleSheets(root: ShadowRoot): boolean {
  return (
    typeof CSSStyleSheet !== 'undefined' &&
    'adoptedStyleSheets' in root &&
    // jsdom exposes the property but cannot always replaceSync.
    typeof CSSStyleSheet.prototype.replaceSync === 'function'
  );
}

/**
 * Applies `css` to `root`, reusing the sheet/element from a previous call for
 * the same root rather than appending another one each time.
 */
function applyToRoot(root: ShadowRoot, css: string): void {
  const existing = adoptedRoots.get(root);

  if (existing) {
    if (existing.kind === 'sheet') {
      existing.sheet.replaceSync(css);
    } else {
      existing.element.textContent = css;
    }
    return;
  }

  if (supportsAdoptedStyleSheets(root)) {
    try {
      const sheet = new CSSStyleSheet();

      sheet.replaceSync(css);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      adoptedRoots.set(root, { kind: 'sheet', sheet });
      return;
    } catch {
      // Fall through to the <style> fallback below.
    }
  }

  const style = document.createElement('style');

  style.textContent = css;
  root.appendChild(style);
  adoptedRoots.set(root, { kind: 'style', element: style });
}

/**
 * Apply the registered CSS to `root`.
 *
 * Prefers constructable stylesheets; falls back to a `<style>` element for
 * environments without full support (notably jsdom, which is what the tests
 * exercise). Never touches `document.head` — that is the whole point.
 */
export function adoptStyles(root: ShadowRoot): void {
  applyToRoot(root, getRegisteredStyles());
}

/**
 * Detaches the adopted styles from `root` and stops tracking it.
 *
 * Call this when a shadow root's owner is going away (the custom element's
 * `disconnectedCallback`); otherwise `adoptedRoots` -- a plain `Map`, so it
 * holds a strong reference -- keeps every root alive for the life of the page
 * in an app that mounts and unmounts many instances over time.
 *
 * Actually removes the sheet/element rather than just forgetting about it: the
 * element's `connectedCallback` re-adopts on every connect, including a
 * reconnect of a previously-disconnected instance, and re-adopting onto a root
 * that still physically has the old styles would leave two copies applied.
 */
export function releaseStyles(root: ShadowRoot): void {
  const existing = adoptedRoots.get(root);

  if (!existing) return;

  if (existing.kind === 'sheet') {
    root.adoptedStyleSheets = root.adoptedStyleSheets.filter(
      (sheet) => sheet !== existing.sheet
    );
  } else {
    existing.element.remove();
  }

  adoptedRoots.delete(root);
}
